import React from 'react';
import { BaseStore, hookFactory, isValidType, itemCreator } from '@platform/template';
import { getLangCode, getSafeRandom, sum, changeTime, formatNumber, formatDatetime, formatAcuracy } from '@platform/api';
import { dfs, formatTree, COLUMN_KEY, IS_LEAF_KEY, isLeafNode, timeTypes, createTree, ROW_STATUS, ERROR_SCALE, presetColumns, DEFAULT_COLOR, switchTypes } from './utils';
import classNames from 'classnames';

const loginLanguage = getLangCode();

function formatColumns(tree, callback) {
    return formatTree(tree, callback, {
        idKey: COLUMN_KEY,
        isLeafKey: IS_LEAF_KEY,
    });
}

/** 
 * 操作表格数据/状态的Store类 
*/
export class TableStore extends BaseStore {
    /**
     * 创建Store实例
     * @param {array} storeExtensions - Store扩展
     */
    constructor(storeExtensions = []) {
        super();

        this.hookExtensions = storeExtensions.reduce((hookExtensions, extension) => {
            Object.assign(hookExtensions, extension.hooks?.call(this));
            return hookExtensions;
        }, {});
        this.propsGenerators = storeExtensions.map(e => e.propsGenerator).filter(e => e);

        Object.assign(this, this.hookExtensions);
    }

    itemIDManager = new IDManager(['rowKey', 'colKey'])

    /**
     * 表格相关状态
     * @type {Store}
     */
    store = {
        // 列信息
        columns: [], // 树形结构，组件用到的结构
        visibleColumns: [], // 树形结构，组件用到的结构
        leafColumnKeys: [], // 只存key
        visibleLeafColumnKeys: [], // 只存key
        columnsMap: {},
        columnWidthOffsets: [],
        columnKeys: [], // 只存key
        // 行信息
        rowKeys: [],
        rowDataMap: {},
        cellPropsMap: {},
        visibleRowKeys: [], 
    }

    /**
     * 设置元数据
     * @function
     * @param {Meta} meta - 元数据
     * @param {string} mainId - 主表id
     * @param {boolean} [shouldUpdate=true] - 是否需要更新
     */
    setMeta = (meta, mainId, shouldUpdate = true) => {
        this.store.status = this.store.status || meta[mainId].status || 'browse';
        this.setColumns(meta[mainId].items, { shouldUpdate });
        Object.assign(this.store, { meta: { ...meta, mainId } });
    }

    /**
     * 获取元数据
     * @function
     * @returns {Meta}
     */
    getMeta = () => {
        let mainId = this.store.meta?.mainId;
        
        if(mainId){
            let meta = this.store.meta;
            meta = {
                ...meta,
                [mainId]: {
                    ...meta[mainId],
                    items: meta[mainId].items.map(item => 
                        this.store.columnsMap[item.attrcode] ? { ...item, ...this.store.columnsMap[item.attrcode] } : item,
                    ),
                },   
            };
            return meta;
        }

        return this.store.meta;
    }

    /**
     * 设置列信息
     * @function
     * @param {MetaItem[]} columns - 列信息
     * @param {Object} [config={}] - 配置项
     * @param {boolean} [config.shouldUpdate=true] - 是否需要更新
     */
    setColumns = (columns, { shouldUpdate = true } = {}) => {
        let changedColumns = [], rawColumns = this.store.columnsMap;

        // 格式化columns，保证fixed在两端
        let left = [], middle = [], right = [];
        columns.forEach(column => {
            if (column.hasOwnProperty('islock') && !column.hasOwnProperty('fixed')) {
                if (column.islock) {
                    left.push(column);
                } else {
                    middle.push(column);
                }
                return;
            }

            switch (column.fixed) {
                case 'left':
                case true:
                    left.push(column);
                    break;
                case 'right':
                    right.push(column);
                    break;
                default:
                    middle.push(column);
                    break;
            }
        });

        columns = [].concat(left, middle, right);

        let columnsInfo = formatColumns(columns, column => {
            // 补全属性
            let { 
                attrcode: key, 
                itemtype, 
                label, 
                width, 
                istotal, 
                isSort: sortable = true, 
                allowLocation: locatable = true, 
                filterable = true,
                color,
                ishighlight,
                required,
            } = column;

            let defaultProps = { 
                visible: false, 
                key, 
                resizable: !column.islock,
                selectable: !presetColumns.has(key), // 用于框选
            };
            
            if (isValidType(itemtype)) {
                defaultProps = {
                    ...defaultProps,
                    sortable, 
                    locatable, 
                    filterable, 
                };
            }

            if (timeTypes.has(itemtype)) {
                defaultProps.autoOpenWhenFocus = false;
            } 

            let mergedColumn = { ...defaultProps, ...column };

            if ( mergedColumn.visible !== rawColumns?.[key]?.visible ||
                itemtype !== rawColumns?.[key]?.itemtype || 
                mergedColumn.scale !== rawColumns?.[key]?.scale
            ) {
                // 类型 、 显隐 、 精度 变化时，需要格式化数据
                changedColumns.push(key);
            }

            if (isLeafNode(column, { isLeafKey: IS_LEAF_KEY })) {
                mergedColumn.width = isNaN(parseInt(width)) ? this._getDefaultWidth(itemtype) : parseInt(width);
            }

            if (column.hasOwnProperty('islock') && !column.hasOwnProperty('fixed')) {
                mergedColumn.fixed = !!column.islock;
            }

            // 合计行
            if (istotal) {
                mergedColumn.total = () => {
                    return this.getColumnProps(key, 'totalValue');
                };
            }

            mergedColumn.title = <div 
                className={`table-next-header-title header-type-${itemtype}`}
                style={{ color: DEFAULT_COLOR.has(color) ? '' : color }}
            >
                <span 
                    className={classNames('table-next-header-content', { 'high-light': ishighlight })}
                    title={typeof label === 'string' && label}
                >
                    {required && <i className='required-column iconfont icon-xinghao'></i>}
                    {typeof label === 'function' ? label(column) : label}
                </span>
            </div>;

            this.store.columnsMap[key] = mergedColumn;

            return mergedColumn;
        });

        // console.log(11111111, columnsInfo);

        this.store.columns = columnsInfo.tree;
        this.store.columnKeys = columnsInfo.nodes.map(column => column.key);
        this.store.leafColumnKeys = columnsInfo.leafNodes.map(column => column.key);

        this._updateVisibleColumns();
        this._formatRows(changedColumns);
        this._updateTotal();

        shouldUpdate && this.forceUpdate();
    }

    /**
     * 获取列信息
     * @function
     * @returns {MetaItem[]}
     */
    getColumns = () => {
        return dfs(this.store.columns, column => {
            return { ...column, ...this.store.columnsMap[column.key] };
        });
    }

    /**
     * 获取可见列
     * @function
     * @returns {MetaItem[]}
     */
    getVisibleColumns = () => {
        return this.store.visibleColumns;
    }

    /**
     * 获取可见叶子列的key
     * @function
     * @returns {ColKey[]}
     */
    getVisibleLeafColumnKeys = () => {
        return this.store.visibleLeafColumnKeys;
    }

    /**
     * 直接修改引用，否则需要反复序列化
     */
    /**
     * 设置某一列的属性
     * @function
     * @param {ColKey} colKey - 列的key
     * @param {ColProps} props - 要设置的属性
     * @param {Object} [config={}] - 配置项
     * @param {boolean} [config.shouldUpdate=true] - 是否需要更新
     */
    setColumnProps = (colKey, props, { shouldUpdate = true } = {}) => {
        if (!this.store.columnsMap[colKey]) return;

        Object.assign(this.store.columnsMap[colKey], props);

        if (props.hasOwnProperty('visible')) {
            this._updateVisibleColumns();
            this._formatRows([colKey]);
            this._updateTotal();
        }
        
        shouldUpdate && this.forceUpdate();
    }

    /**
     * 获取某一列的属性
     * @function
     * @param {ColKey} colKey - 列的key 
     * @param {string} [propName] - 要获取的属性名。为空时获取该列所有属性
     * @returns {ColPropValue|MetaItem}
     */
    getColumnProps = (colKey, propName) => {
        let columnInfo = this.store.columnsMap[colKey];
        return propName ? columnInfo?.[propName] : columnInfo ?? {};
    }

    /**
     * 设置行数据
     * @function
     * @param {TableData} tableData - 表格数据
     * @param {Object} [config={}] - 配置项 
     * @param {boolean} [config.format=true] - 是否需要格式化数据 
     * @param {string} config.rowKey - 行数据中作为唯一标识的字段 
     */
    setRows = (tableData, { format = true, rowKey } = {}) => {
        let rowDataMap = {}, 
            formattedKeys = this.getVisibleLeafColumnKeys(),
            normalRowKeys = [],
            deleteRowKeys = [];

        // pageinfo的逻辑是借鉴以前的代码，很奇怪
        if (!tableData?.pageInfo) {
            if (tableData?.rows?.length) {
                tableData.pageInfo = this.getPaginationInfo();
            } else {
                tableData.pageInfo = {
                    pageSize: 0,
                    pageIndex: 0,
                    total: 0,
                    totalPage: 1,
                };
            }
        }

        if (tableData.allpks?.length) {
            this.store.allpks = tableData.allpks;
        } else {
            // allpks的逻辑是借鉴以前的代码，很奇怪
            if (!tableData?.rows?.length) {
                this.store.allpks = [];
            } 
        }

        if (!format) {
            // number类型始终format， 因为display不可枚举，容易丢失
            formattedKeys = formattedKeys.filter(key => this.getColumnProps(key, 'itemtype') === 'number');
        }

        tableData.rows.forEach(row => {
            rowKey && (row.key = row.values[rowKey]?.value);
            this._formatRowData(row, formattedKeys);

            let key = row.key;

            row = { 
                ...row, 
                pid: 'root',
                isLeaf: true,
                depth: 0,
                path: [key],
                height: 0,
            };

            if(row.status === ROW_STATUS.DELETE) {
                deleteRowKeys.push(key);
            } else {
                normalRowKeys.push(key);
            }

            rowDataMap[key] = row;

            return row;
        });

        Object.assign(this.store, { 
            rowDataMap, 
            rowKeys: normalRowKeys.concat(deleteRowKeys),
            visibleRowKeys: normalRowKeys,
        });

        this._updateDataIndex();
        this._updateViewIndex();
        this._updateTotal();
        this.updateRowsHeight({ shouldUpdate: false });

        this.forceUpdate();
    }

    /**
     * 获取行数据
     * @function
     * @returns {TableRow[]}
     */
    getRows = () => {
        return this.store.rowKeys.map(this.getRowData);
    }

    /**
     * 设置allpks
     * @function
     * @param {TableData} tableData - 表格数据 
     * @param {Object} [config={}] - 配置项
     * @param {boolean} [config.shouldUpdate=true] - 是否需要更新
     */
    setAllRowsInfo = (tableData, { shouldUpdate = true } = {}) => {
        if (tableData.allpks) {
            this.store.allpks = tableData.allpks;
        }

        if (tableData.alltss) {
            Object.assign(this.store, { alltss: tableData?.alltss });
        }

        shouldUpdate && this.forceUpdate();
    }
    
    /** 
     * 获取allpks
     * @function
     * @returns {{ allpks: string[], alltss: string[] }>}
    */
    getAllRowsInfo = () => {
        return {
            allpks: this.store.allpks || [],
            alltss: this.store.alltss || [],
        };
    }
    
    /**
     * 获取可见行
     * @function
     * @returns {TableRow[]}
     */
    getVisibleRows = () => {
        return this.store.visibleRowKeys.map(this.getRowData);
    }

    /**
     * 更新行数据
     * 注意：此方法不能传未知的rowKey（新增行），因为不知道新增行的index
     * @function
     * @param {TableRow[]} rowsData - 需要更新的行数据
     * @param {Object} [config={}] - 配置项 
     * @param {boolean} [config.format=true] - 是否需要格式化数据 
     * @param {string} config.rowKey - 行数据中作为唯一标识的字段 
     */
    setRowData = (rowsData, { shouldUpdate = true, rowKey } = {}) => {
        !Array.isArray(rowsData) && (rowsData = [rowsData]);

        rowsData.forEach(rowData => {
            let key = rowData.key || rowData.rowId || rowData.rowid || (rowKey && rowData.values[rowKey]?.value);
            let currentRowData = this.store.rowDataMap[key];
            if (currentRowData) {
                rowData = { 
                    ...currentRowData, 
                    ...rowData,
                    values: {
                        ...currentRowData.values, 
                        ...rowData.values,
                    },
                };

                this.store.rowDataMap[key] = this._formatRowData(rowData);

                shouldUpdate && this.store.leafColumnKeys.map(colKey => {
                    this.updateComponent(this.itemIDManager.getID({ key, colKey }));
                });
            } else {
                console.warn('rowKey输入有误');
            }
        });

        this._updateTotal();

        shouldUpdate && this.forceUpdate();        
    }

    /**
     * 获取行数据
     * @function
     * @param {RowKey} rowKey 
     * @returns {TableRow}
     */
    getRowData = rowKey => {
        return this.store.rowDataMap[rowKey];
    }

    /**
     * 获取所有行的rowKey
     * @function
     * @returns {RowKey[]}
     */
    getRowKeys = () => {
        return this.store.rowKeys;
    }

    /**
     * 获取所有可见行的rowKey
     * @function
     * @returns {RowKey[]}
     */
    getVisibleRowKeys = () => {
        return this.store.visibleRowKeys;
    }

    /**
     * 增行
     * @function
     * @param {DataIndex} dataIndex 
     * @param {TableRow[]} nextRows 
     * @param {Object} config 
     * @todo 参数描述
     */
    addRow = (dataIndex, nextRows, config = {}) => {
        !Array.isArray(nextRows) && (nextRows = [nextRows]);

        let rows = this.getRows(),
            addedRows = [];
        
        if (Array.isArray(dataIndex)) {
            // 效率优化，合并多次增行操作
            // 调用方式：addRow([{ dataIndex, row }], config)
            nextRows = dataIndex;
            config = nextRows || {};
        } else {
            // 调用方式：addRow(dataIndex, [row], config)
            dataIndex = Math.min(Math.max(0, dataIndex ?? rows.length), rows.length);
            nextRows = nextRows.map((row, index) => {
                return {
                    dataIndex: dataIndex + index,
                    row,
                };
            });
        }

        if (!nextRows.length) return;

        let { autoFocus = true, rowKey, changeStatus = true } = config;

        let { leafColumnKeys } = this.store;

        nextRows.forEach(({ dataIndex, row: nextRow }) => {
            // 防止改引用，但是要注意性能
            nextRow = JSON.parse(JSON.stringify(nextRow));
            nextRow.status = ROW_STATUS.ADD;

            nextRow.values = nextRow.values || {};

            rowKey && (nextRow.key = nextRow.values[rowKey]?.value);

            // 添加模板默认值
            leafColumnKeys.forEach(colKey => {
                if (!nextRow.values[colKey] || !Object.keys(nextRow.values[colKey]).length) {
                    let col = this.getColumnProps(colKey);
                    let defaultCreator = itemCreator({ itemtype: col.itemtype });
                    nextRow.values[colKey] = {
                        ...defaultCreator.defaultValue,
                        ...col.initialvalue,
                    };
                }
            });

            let formattedRow = this._formatRowData(nextRow);

            // 增行时，如果key重复了，生成新的key
            if (this.getRowData(formattedRow.key)) {
                formattedRow.key = formattedRow.rowid = formattedRow.rowId = this._makeRowKey();
                formattedRow.path = [formattedRow.key];
            }

            this.store.rowDataMap[formattedRow.key] = formattedRow;
            addedRows.push(formattedRow);
            
            // 更新rowKeys
            rows.splice(dataIndex, 0, formattedRow);
            this.store.rowKeys.splice(dataIndex, 0, formattedRow.key);

            // 更新visibleRowKeys
            let viewIndex;
            if (dataIndex > 0) {
                viewIndex = rows[dataIndex - 1].viewIndex + 1;
            } else {
                viewIndex = 0;
            }
            this.store.visibleRowKeys.splice(viewIndex, 0, formattedRow.key);
            
            this._updateViewIndex(viewIndex); // 这里需要更新viewIndex，下一次循环才能取到值
        });
        
        this._updateDataIndex();
        this._updateTotal();
        this.updateRowsHeight({ shouldUpdate: false });
        
        changeStatus ? this.setStatus('edit') : this.forceUpdate();

        let visibleLeafColumnKeys = this.store.visibleLeafColumnKeys.filter(
            key => isValidType(this.getColumnProps(key, 'itemtype')) && !this.getColumnProps(key, 'disabled'),
        );
        requestAnimationFrame(() => {
            if (autoFocus) {
                // 滚动到新行，并聚焦第一个元素
                this.focusOnCell(addedRows?.[0].key, visibleLeafColumnKeys[0]);
            } else {
                this.scrollIntoView({ rowKey: addedRows?.[0].key });
            }
        });
    }

    /**
     * 删行
     * @function
     * @param {RowKey[]} rowKeys - 需要删除的rowKeys
     * @param {boolean} deepDelete - 是否是真删
     */
    deleteRow = (rowKeys, deepDelete) => {
        let keySet = new Set(Array.isArray(rowKeys) ? rowKeys : [rowKeys]),
            normalRowKeys = [],
            deleteRowKeys = [],
            { crossPageSelect, pkname } = this.getConfig(),
            allCheckedKeys = this.getAllCheckedKeys();
            
        // 更新rowKeys
        this.store.rowKeys.forEach(rowKey => {
            if (keySet.has(rowKey)) {
                let row = this.getRowData(rowKey);
                if (!deepDelete && row.status !== ROW_STATUS.ADD) {
                    // 假删
                    row.status = ROW_STATUS.DELETE;
                    deleteRowKeys.push(rowKey);
                }

                if (crossPageSelect && pkname) {
                    allCheckedKeys.delete(row.values[pkname]?.value);
                }

            } else {
                normalRowKeys.push(rowKey);
            }
        });

        this.store.rowKeys = normalRowKeys.concat(deleteRowKeys);
        this.setAllCheckedKeys(allCheckedKeys);

        this._updateDataIndex();

        // 更新visibleRowKeys
        this.store.visibleRowKeys = this.store.visibleRowKeys.filter(rowKey => !keySet.has(rowKey));
        this._updateViewIndex();
        this._updateTotal();
        this.updateRowsHeight({ shouldUpdate: false });

        this.forceUpdate();
    }

    /**
     * 移动行（保持其他行相对顺序不变）
     * @function
     * @param {number} startDataIndex - 起始位置
     * @param {number} endDataIndex - 结束位置
     */
    moveRow = (startDataIndex, endDataIndex) => {
        this._rowTransform([startDataIndex, endDataIndex], (rowKeys, [startIndex, endIndex]) => {
            let targetKey = rowKeys[startIndex];

            if(startIndex < endIndex) {
                for (
                    let i = startIndex + 1;
                    i <= endIndex;
                    i++
                ) {
                    rowKeys[i - 1] = rowKeys[i];
                }
            } else {
                for (
                    let i = startIndex - 1;
                    i >= endIndex;
                    i--
                ) {
                    rowKeys[i + 1] = rowKeys[i];
                }

            }

            rowKeys[endIndex] = targetKey;
            return rowKeys; 
        });

        this._updateDataIndex();
        this._updateViewIndex();
        this.updateRowsHeight({ shouldUpdate: false });

        this.forceUpdate();
    }

    /**
     * 交换两行的位置
     * @function
     * @param {number} startDataIndex - 起始位置
     * @param {number} endDataIndex - 结束位置
     */
    swapRow = (startDataIndex, endDataIndex) => {
        this._rowTransform([startDataIndex, endDataIndex], (rowKeys, [startIndex, endIndex]) => {
            let targetKey = rowKeys[startIndex];
            rowKeys[startIndex] = rowKeys[endIndex];
            rowKeys[endIndex] = targetKey;
            return rowKeys;
        });

        this._updateDataIndex();
        this._updateViewIndex();
        this.updateRowsHeight({ shouldUpdate: false });

        this.forceUpdate();
    }

    /**
     * 更新合计行（由外界不可控因素导致合计行需要变化时，需要调用此方法）
     * @function
     */
    updateTotal = () => {
        this._updateTotal();
        this.forceUpdate();
    }

    /**
     * 设置多语
     * @function
     * @private
     * @param {Intl} intl - 多语对象
     */
    setLocale = intl => {
        this.store.intl = intl;

        // 开关类型的「是/否」需要重新格式化
        let formatColumns = this.getVisibleLeafColumnKeys().filter(key => switchTypes.has(this.getColumnProps(key, 'itemtype')));

        this._formatRows(formatColumns);
        this.forceUpdate();
    }

    /**
     * 获取多语
     * @function
     * @private
     * @returns {Intl}
     */
    getLocale = () => {
        return this.store.intl || {};
    }
    
    /**
     * 获取hooks
     * @function
     * @private
     * @returns {TableHooks}
     */
    getHooks = () => {
        return {
            setMeta: this.setMeta,
            getMeta: this.getMeta,
            setColumns: this.setColumns,
            getColumns: this.getColumns,
            getVisibleColumns: this.getVisibleColumns,
            getVisibleLeafColumnKeys: this.getVisibleLeafColumnKeys,
            setColumnProps: this.setColumnProps,
            getColumnProps: this.getColumnProps,
            setRows: this.setRows,
            getRows: this.getRows,
            setAllRowsInfo: this.setAllRowsInfo,
            getAllRowsInfo: this.getAllRowsInfo,
            getVisibleRows: this.getVisibleRows,
            setRowData: this.setRowData,
            getRowData: this.getRowData,
            getRowKeys: this.getRowKeys,
            getVisibleRowKeys: this.getVisibleRowKeys,
            addRow: this.addRow,
            deleteRow: this.deleteRow,
            moveRow: this.moveRow,
            swapRow: this.swapRow,
            setLocale: this.setLocale,
            getLocale: this.getLocale,
            updateTotal: this.updateTotal,
            ...this.hookExtensions,
        };
    }

    getPropsGenerator = () => {
        return this.propsGenerators;
    }

    _makeRowKey = () => String(new Date().getTime()).slice(-5) + getSafeRandom().toString(12)

    _formatRows = columns => {
        let { rowKeys, rowDataMap } = this.store;
        
        // 根据meta，重新格式化数据
        rowKeys.forEach(rowKey => {
            rowDataMap[rowKey] = this._formatRowData(rowDataMap[rowKey], columns);
        });
    }

    _formatRowData = (row, columns) => {
        let { leafColumnKeys, visibleLeafColumnKeys } = this.store,
            visibleKeySet = new Set(visibleLeafColumnKeys),
            key = row.rowid || row.rowId || row.key || this._makeRowKey();

        row.rowid = row.rowId = row.key = key; 
        row.status = row.status || ROW_STATUS.ORIGIN;

        row.values = (columns || leafColumnKeys).reduce((values, colKey) => {
            // 补全数据
            if (!values[colKey]) {
                values[colKey] = {};
            }

            // 对可见单元格的格式化
            if (visibleKeySet.has(colKey)) {
                values[colKey] = this._formatCellValue(values, colKey);
                // 取消校验状态
                if (values[colKey]?.value && this.getCellProps(row.key, colKey, 'invalid')) {
                    this.setCellProps(row.key, colKey, { invalid: false });
                    this.clearCellValidate(row.key, colKey);
                }
            }
            return values;
        }, row.values || {});

        row = this._formatInputRow(row);

        return row;
    }

    _formatCellValue = (values, colKey, itemtype) => {
        let cellValue = values[colKey] ?? { display: '', value: '' };

        let column = this.getColumnProps(colKey),
            { isconverttimezones, languageMeta, attrcode, options, multiple } = column;
        
        itemtype = itemtype ?? column.itemtype;

        // radio和checkbox没有multiple,手动处理下
        if (itemtype === 'radio') {
            multiple = false;
        } else if (itemtype === 'checkbox') {
            multiple = true;
        }

        // 非对象时，不转化
        if (itemtype && Object.prototype.toString.call(cellValue).slice(8, -1) === 'Object') {
            let { display, value } = cellValue;

            /**
             * ———————————————————— 和itemCreator保持一致 ————————————————————
             */
            switch (itemtype) {
                case 'label':
                    display = cellValue.display ?? cellValue.value;
                    break;
                case 'number':
                    display = formatNumber(formatAcuracy(value, this._getScale(cellValue, column)), true);
                    break;
                case 'password':
                    display = '*'.repeat(8);
                    break;
                case 'refer':
                    display = cellValue.display ?? cellValue.value;
                    break;
                case 'radio':
                case 'select':
                case 'checkbox':
                    if (display) break;
                    if (multiple) {
                        display = (value && typeof value === 'string' ? value.split(',') : []).map(val => options?.find(e => e.value === val)?.display || (itemtype === 'select' ? value ?? '' : '')).join(',');
                    } else {
                        display = options?.find(e => e.value === value)?.display || (itemtype === 'select' ? value ?? '' : '');
                    }
                    break;
                case 'NCTZDatePickerStart':
                case 'NCTZDatePickerEnd':
                case 'NCTZDatePickClientHourTime':
                case 'datepicker':
                    display = changeTime(value, 'YYYY-MM-DD', isconverttimezones);
                    display = formatDatetime(display, 'date');
                    break;
                case 'datePickerNoTimeZone':
                    display = formatDatetime(value, 'date');
                    break;
                case 'timepicker':
                    display = formatDatetime(value, 'time');
                    break;
                case 'datetimepicker':
                case 'NCTZDatePickClientTime':
                    display = changeTime(value, 'YYYY-MM-DD HH:mm:ss', isconverttimezones);
                    display = formatDatetime(display, 'datetime');
                    break;
                case 'residtxt':
                    // 优先展示登录语言值，无值时展示主语言值
                    let loginValue,
                        mainValue;
                    Array.isArray(languageMeta) && languageMeta.map(({ index, languageCode }) => {
                        if (languageCode === loginLanguage) {
                            // 登录语言值
                            let realCode = index == 1 ? attrcode : attrcode + index;
                            loginValue = values[realCode];
                        }
                        if (index == 1 || index == '') {
                            // 主语言值
                            mainValue = values[attrcode];
                        }
                    });
                    display = loginValue?.value || mainValue?.value || '';
                    break;
                case 'checkbox_switch':
                case 'switch':
                case 'switch_browse':
                    let locale = this.getLocale();

                    if (value === 'Y') {
                        display = locale['table_yes' /*是*/];
                    } else if (value === 'N') {
                        display = locale['table_no' /*否*/];
                    } else {
                        display = value ? locale['table_yes' /*是*/] : locale['table_no' /*否*/];
                    }

                    break;
                default:
                    display = display ?? value;
            }
            /**
             * ———————————————————— 和itemCreator保持一致 ————————————————————
             */

            cellValue = { ...cellValue, _display_: display };

            return cellValue;
        } 

        return cellValue;
    }

    _getScale = (cellValue, column) => {
        return Number(ERROR_SCALE.has(cellValue?.scale) ? ERROR_SCALE.has(column?.scale) ? 0 : column?.scale : cellValue?.scale) ?? 0;
    }

    // 格式化输入的行
    _formatInputRow = row => row

    // 格式化输出的行
    _formatOutputRow = row => row

    _delete = visibleRowKeys => {
        return visibleRowKeys.filter(rowKey => {
            let row = this.getRowData(rowKey);
            return row.status !== ROW_STATUS.DELETE;
        });
    }

    _rowTransform = (dataIndexes, callback, mapIndex) => {
        if (!mapIndex) {
            // dataIndex如何转换成viewIndex
            let rows = this.getRows();
            mapIndex = dataIndex => this.getRowData(rows[dataIndex].key).viewIndex;
        }
        let viewIndexes = dataIndexes.map(mapIndex);

        this.store.rowKeys = callback(this.store.rowKeys, dataIndexes);
        this.store.visibleRowKeys = callback(this.store.visibleRowKeys, viewIndexes);
    }

    // 根据全数据、过滤、排序，得出新的视图数据
    _updateVisibleRows = () => {
        let visibleRowKeys = [
            this._delete,
            this._filter,
            this._sort,
        ].reduce((visibleRowKeys, step) => step ? step(visibleRowKeys) : visibleRowKeys, this.store.rowKeys);

        this.store.visibleRowKeys = visibleRowKeys;

        this._updateViewIndex();
        this.updateRowsHeight({ shouldUpdate: false });
    }

    _updateDataIndex = () => {
        this.store.rowKeys.forEach((rowKey, index) => {
            Object.assign(this.store.rowDataMap[rowKey], { 
                pid: 'root',
                isLeaf: true,
                depth: 0,
                path: [rowKey],
                dataIndex: index,
                leafIndex: index,
                height: 0,
                range: [index, index + 1],
            });
        });
    }

    // visibleRowKeys变化后的回调，更新index和rowsHeight
    _updateViewIndex = (startIndex = 0) => {
        // 更新viewIndex
        for (let index = startIndex; index < this.store.visibleRowKeys.length; index++) {
            const rowKey = this.store.visibleRowKeys[index];
            this.store.rowDataMap[rowKey].viewIndex = index;

            /**
             * 兼容：
             *  - 业务组会用row.values.numberindex.value取序号
             *  - values的判断为了兼容SimpleTable
             */
            if (this.store.rowDataMap[rowKey].values) {
                this.store.rowDataMap[rowKey].values.numberindex = { _display_: String(index + 1), value: String(index + 1) };
            } else {
                this.store.rowDataMap[rowKey].numberindex = { _display_: String(index + 1), value: String(index + 1) };
            }
        }
    }

    _updateVisibleColumns = () => {
        let visibleTree = createTree(this.store.columnKeys.reduce((flatColumns, key) => {
            let columnInfo = this.getColumnProps(key);
            if (columnInfo.visible) {
                flatColumns.push(columnInfo);
            }
            return flatColumns;
        }, []), { idKey: COLUMN_KEY });

        let visibleColumnsInfo = formatColumns(visibleTree, column => {
            let { key } = column;
            this.store.columnsMap[key] = column;
            return column;
        });

        // console.log(3333333333, visibleColumnsInfo);
        this.store.visibleColumns = visibleColumnsInfo.tree;
        this.store.visibleColumnKeys = visibleColumnsInfo.nodes.map(e => e.key);
        this.store.visibleLeafColumnKeys = visibleColumnsInfo.leafNodes.map(e => e.key);

        this.updateColumnsWidth({ shouldUpdate: false });
    }
    
    _updateTotal = () => {
        let visibleRows = this.getVisibleRows(),
            rows = this.getRows(),
            { noTotalRows, noTotalCol = [], allowTotalRows, showTotal } = this.getComponent('Table')?.current?.props ?? {},
            totalColumns = this.getVisibleLeafColumnKeys().filter(key => 
                this.getColumnProps(key, 'istotal') || this.getColumnProps(key, 'totalRender'),
            );

        // 参与合计的行：() => [viewIndex]
        let totalRows;
        if (typeof allowTotalRows === 'function') {
            totalRows = new Set(allowTotalRows()?.map(dataIndex => rows[dataIndex].viewIndex));
            // TODO:下面有空间做一点性能优化，但是要处理值改变但传了allowTotalRows的情况，先注释掉
            // if (
            //     this.lastTotalRows && 
            //     this.lastTotalRows.size === totalRows.size && 
            //     new Set([...this.lastTotalRows, ...totalRows]).size === this.lastTotalRows.size
            // ) {
            //     // 需要合计的行未改变，不更新
            //     return;
            // }
            // this.lastTotalRows = totalRows;
        }

        // 每一行中不参与合计的列：() => [{ code: [] | String, indexArr: String }]
        let excludes = {};
        if (typeof noTotalRows === 'function') {
            noTotalRows()?.forEach(({ code, indexArr: viewIndex }) => {
                // 不知道code是字符串还是数组
                excludes[viewIndex] = code;
            });
        }

        // 不参与合计的列
        noTotalCol = new Set(noTotalCol);

        totalColumns.forEach(key => {
            if (noTotalCol.has(key)) {
                return;
            }

            let column = this.getColumnProps(key);
            if (!visibleRows?.length) {
                this.setColumnProps(key, { totalValue: '' }, { shouldUpdate: false });
            }

            // totalScale: 这一列的最大精度
            let values = [], totalScale = 0;
            visibleRows.forEach((row, viewIndex) => {
                if (excludes?.[viewIndex]?.includes(key)) {
                    return;
                }

                if (totalRows && !totalRows.has(viewIndex)) {
                    return;
                }
                
                let value = row.values[key]?.value ?? 0;

                values.push(value);
                totalScale = Math.max(totalScale, this._getScale(row.values[key], column));
            });

            // 用户自定义合计
            let totalRender = this.getColumnProps(key, 'totalRender'),
                totalValue = formatNumber(formatAcuracy(sum(...values), totalScale));

            // 合计、千分位、精度
            this.setColumnProps(
                key, 
                { totalValue: totalRender ? totalRender(key, visibleRows, totalValue) : totalValue }, 
                { shouldUpdate: false },
            );
        });
    }

    _getDefaultWidth = itemtype => {
        if (itemtype === 'datetimepicker') {
            return 240;
        } else if (itemtype === 'rangepicker') {
            return 390;
        } else if (timeTypes.has(itemtype)) {
            return 160;
        } else {
            return 120;
        }
    }

    _updateRows = () => {
        let normalRowKeys = [], deleteRowKeys = [];

        this.store.rowKeys.forEach(rowKey => {
            let row = this.store.rowDataMap[rowKey];

            if(row.status === ROW_STATUS.DELETE){
                deleteRowKeys.push(rowKey);
            } else {
                normalRowKeys.push(rowKey);
            }
        });

        Object.assign(this.store, {
            rowKeys: normalRowKeys.concat(deleteRowKeys),
            visibleRowKeys: normalRowKeys,
        });

        this._updateDataIndex();
        this._updateViewIndex();
        this.updateRowsHeight({ shouldUpdate: false });
    }
}

export default hookFactory(TableStore);

class IDManager {
    constructor(keys = [], { sep = '-' } = {}) {
        this.keys = keys;
        this.sep = sep;
    }

    getParam = id => {
        let ids = id.split(this.sep);
        return this.keys.reduce((param, key, index) => {
            param[key] = ids[index];
            return param;
        }, {});
    }

    getID = param => {
        return this.keys.map(key => param[key]).join(this.sep);
    }
}